import { useEffect, useMemo } from "react"
import { UseFormReturn, useForm } from "react-hook-form"

import { useAssignRef } from "shared/lib/useAssignRef"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  ProjectFormData,
  ProjectFormDataSchema,
  ProjectFormState
} from "features/project-management/types/ProjectFormData"

import { ProjectFormProps } from "../ProjectForm"

export interface ProjectFormViewModel {
  form: UseFormReturn<ProjectFormState, unknown, ProjectFormData>
  onSubmit: () => void
}

export function useProjectFormViewModel({
  defaultFormData,
  onSubmit,
  onChange,
  formRef,
  isReadonly
}: ProjectFormProps): ProjectFormViewModel {
  const form = useForm<ProjectFormState, unknown, ProjectFormData>({
    defaultValues: defaultFormData,
    resolver: zodResolver<ProjectFormState, unknown, ProjectFormData>(
      ProjectFormDataSchema
    )
  })

  useAssignRef({ ref: formRef, value: form })

  useEffect(() => {
    if (!onChange) return undefined
    return form.subscribe({
      callback: ({ values }) => {
        onChange?.(values)
      },
      formState: { values: true }
    })
  }, [form, onChange])

  useEffect(() => {
    form.reset(defaultFormData, { keepDirty: true, keepDirtyValues: true })
  }, [defaultFormData])

  useEffect(() => {
    if (isReadonly) {
      form.reset(defaultFormData)
    }
  }, [isReadonly])

  const handleSubmit = useMemo(() => {
    return form.handleSubmit(formData => onSubmit?.(formData))
  }, [form, onSubmit])

  return {
    form,
    onSubmit: handleSubmit
  }
}
